import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import usePut from "../usePut";
import toast from "react-hot-toast";
import useRead from "../useRead";
import { roundToTwoDecimalPlaces } from "../../utility/utils";

const useManageSalaryTemplate = ({ id }) => {
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState("");
  const { control, handleSubmit, setValue, register, watch, getValues } =
    useForm({
      defaultValues: {
        annual_ctc: 0,
        earnings: [],
        statutory: {},
      },
    });

  const [statutoryComponentsList, setStatutoryComponentsList] = useState([]);

  // Fetch Statutory Components
  const { list: fetchStatutoryRequest } = useRead({
    url: "statutory-types/list",
    method: "GET",
    initialData: {},
    onSuccess: (data) => {
      const resData = data?.data || [];

      setStatutoryComponentsList(resData);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error fetching statutory components: " + error.message
      );
    },
  });
  const isFetchingStatutory = fetchStatutoryRequest.isLoading;

  //   For adding and removing rows
  const [showAddRowModal, setShowAddRowModal] = useState(false);

  const toggleAddRowModal = () => {
    setShowAddRowModal(!showAddRowModal);
  };

  const addRows = (newRows) => {
    setValue("earnings", [
      ...watch("earnings"),
      ...newRows.map((el) => {
        return {
          component_id: el.component_id,
          name: el.name,
          monthly_value: 0,
          annual_value: 0,
          is_yearly_editable:
            parseInt(el.is_yearly_editable) === 1 ? true : false,
          is_monthly_editable:
            parseInt(el.is_monthly_editable) === 1 ? true : false,
        };
      }),
    ]);

    setShowAddRowModal(false);
  };

  const removeRow = (index) => {
    const currentRows = watch("earnings");
    setValue(
      "earnings",
      currentRows.filter((_, idx) => idx !== index)
    );
  };

  const [othersMonthlyValue, setOthersMonthlyValue] = useState(0);
  const [othersAnnualValue, setOthersAnnualValue] = useState(0);

  const recalculateOthersValue = () => {
    const annualCtc = getValues("annual_ctc") || 0;
    const earningsArr = getValues("earnings") || [];

    const monthlyTotal = earningsArr.reduce((acc, el) => {
      return acc + (parseFloat(el.monthly_value) || 0);
    }, 0);

    const monthlyVal = roundToTwoDecimalPlaces(
      parseFloat(annualCtc) / 12 - monthlyTotal
    );
    setOthersMonthlyValue(monthlyVal);

    // Calculating annual value
    const annualTotal = earningsArr.reduce((acc, el) => {
      return acc + (parseFloat(el.annual_value) || 0);
    }, 0);

    const annualVal = roundToTwoDecimalPlaces(
      parseFloat(annualCtc) - annualTotal
    );
    setOthersAnnualValue(annualVal);
  };

  const calculateVariableValues = () => {
    const earningsArr = getValues("earnings") || [];
    const annualCtc = getValues("annual_ctc") || 0;

    for (let i = 0; i < earningsArr.length; i++) {
      const el = earningsArr[i];

      if (el.calculation_type === "VARIABLE") {
        el.annual_value = roundToTwoDecimalPlaces(
          annualCtc * (el.calculation_value / 100)
        );
        el.monthly_value = roundToTwoDecimalPlaces(el.annual_value / 12);

        setValue(`earnings[${i}].annual_value`, el.annual_value);
        setValue(`earnings[${i}].monthly_value`, el.monthly_value);
      }
    }
  };

  const calculateMonthlyOnAnnualChange = (e, idx) => {
    const annualValue = parseFloat(e.target.value) || 0;
    const monthlyValue = annualValue / 12 || 0;

    const fields = watch("earnings").map(
      (_, i) => `earnings[${i}].annual_value`
    );

    const otherSum = fields
      .filter((f) => f !== `earnings[${idx}].annual_value`)
      .reduce((acc, f) => acc + (parseFloat(getValues(f)) || 0), 0);

    if (otherSum + annualValue <= parseFloat(getValues("annual_ctc"))) {
      setValue(
        `earnings[${idx}].monthly_value`,
        parseFloat(monthlyValue?.toFixed(2))
      );

      setValue(
        `earnings[${idx}].annual_value`,
        parseFloat(annualValue?.toFixed(2))
      );

      recalculateOthersValue();
      return true;
    } else {
      e.preventDefault();
      toast.error("Total components cannot be more than Annual CTC");
      return false;
    }
  };

  const calculateAnnualOnMonthlyChange = (e, idx) => {
    const monthlyValue = parseFloat(e.target.value) || 0;
    const annualValue = monthlyValue * 12 || 0;

    const fields = watch("earnings").map(
      (_, i) => `earnings[${i}].monthly_value`
    );

    const otherSum = fields
      .filter((f) => f !== `earnings[${idx}].monthly_value`)
      .reduce((acc, f) => acc + (parseFloat(getValues(f)) || 0), 0);

    if (otherSum * 12 + monthlyValue <= parseFloat(getValues("annual_ctc"))) {
      setValue(
        `earnings[${idx}].monthly_value`,
        parseFloat(monthlyValue?.toFixed(2))
      );

      setValue(
        `earnings[${idx}].annual_value`,
        parseFloat(annualValue?.toFixed(2))
      );

      recalculateOthersValue();
    } else {
      toast.error("Total components cannot be more than Annual CTC");
      return false;
    }
  };

  const handleCTCChange = (e) => {
    const inputValue = parseFloat(e.target.value) || 0;

    const fields = watch("earnings").map(
      (_, i) => `earnings[${i}].annual_value`
    );

    const totalFields = fields.reduce((acc, f) => {
      return acc + (parseFloat(getValues(f)) || 0);
    }, 0);

    if (inputValue >= totalFields) {
      setValue("annual_ctc", inputValue);
      calculateVariableValues();
      recalculateOthersValue();
      return true;
    } else {
      toast.error("Annual CTC cannot be less than total components");
      return false;
    }
  };

  // To fetch components
  const {} = useRead({
    url: "template-salary-components/fetchtemplateData",
    method: "GET",
    initialData: {
      template_id: id,
    },
    onSuccess: (data) => {
      setTemplateName(data?.template_structure?.name || "");
      setValue("annual_ctc", data?.template_structure?.annual_ctc);
      setValue(
        "earnings",
        data?.earnings?.map((el) => {
          return {
            ...el,
            name: el.component_name,
          };
        }) || []
      );

      setOthersAnnualValue(data?.template_structure?.annual_others);
      setOthersMonthlyValue(data?.template_structure?.monthly_others);

      calculateVariableValues();
      recalculateOthersValue();
    },
    onError: (error) => {
      toast.error("Error fetching template components: " + error?.message);
    },
  });

  const { handleAuth: createUpdateReq } = usePut({
    url: "template-salary-components/create",
    refreshUrl: [
      "dashboard/company_completion_status",
      "is_permission_for_product",
    ],
    onSuccess: () => {
      toast.success("Template saved successfully");
      // if (sessionStorage.getItem("onboarding_flow") === "true") {
      //   sessionStorage.removeItem("onboarding_flow");
      //   navigate("/onboarding");
      // }
    },
    onError: (error) => {
      toast.error("Error creating/updating template: " + error?.message);
    },
  });
  const isSaving = createUpdateReq.isLoading;

  const handleSave = (data) => {
    createUpdateReq.mutate(data);
  };

  return {
    templateName,
    control,
    handleSubmit,
    setValue,
    addRows,
    register,
    showAddRowModal,
    toggleAddRowModal,
    handleCTCChange,
    othersMonthlyValue,
    othersAnnualValue,
    watch,
    handleSave,
    removeRow,
    isSaving,
    statutoryComponentsList,
    isFetchingStatutory,
    // Calculation functions
    calculateMonthlyOnAnnualChange,
    calculateAnnualOnMonthlyChange,
  };
};

export default useManageSalaryTemplate;
